import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import mabarService from '@/services/mabarService';
import type {
  GameDetailResponse,
  GameTimelineEvent,
} from '@/services/communityService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button as ShadcnButton } from '@/components/shadcn/button';

const getTeamInitial = (name?: string): string => {
  if (!name) return 'T';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const normalizeTimeline = (raw: unknown): GameTimelineEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is GameTimelineEvent =>
      e != null && typeof e === 'object' && 'time' in e && 'type' in e && 'team_side' in e
  ) as GameTimelineEvent[];
};

/** Parse time string (e.g. "45", "4'", "90+2") to { minute, added } for sorting. */
const parseTime = (t: string): { minute: number; added: number } => {
  const s = String(t ?? '').trim();
  const plus = s.indexOf('+');
  if (plus === -1) {
    const minute = Number.parseInt(s.replace(/\D/g, ''), 10) || 0;
    return { minute, added: 0 };
  }
  const minute = Number.parseInt(s.slice(0, plus).replace(/\D/g, ''), 10) || 0;
  const added = Number.parseInt(s.slice(plus + 1).replace(/\D/g, ''), 10) || 0;
  return { minute, added };
};

const sortTimelineByTimeDesc = (events: GameTimelineEvent[]): GameTimelineEvent[] => {
  return [...events].sort((a, b) => {
    const ta = parseTime(a.time);
    const tb = parseTime(b.time);
    if (tb.minute !== ta.minute) return tb.minute - ta.minute;
    return tb.added - ta.added;
  });
};

const getScoreFromTimeline = (timeline: GameTimelineEvent[]) => {
  let home = 0;
  let away = 0;
  timeline.forEach((e) => {
    if (e.type === 'goal' || e.type === 'own_goal') {
      if (e.team_side === 'home') home += 1;
      else if (e.team_side === 'away') away += 1;
    }
  });
  return { home, away };
};

const getStatsFromTimeline = (timeline: GameTimelineEvent[]) => {
  let goalsHome = 0;
  let goalsAway = 0;
  let savesHome = 0;
  let savesAway = 0;
  let foulsHome = 0;
  let foulsAway = 0;
  const foulTypes = ['foul', 'yellow_card', 'red_card'];
  timeline.forEach((e) => {
    if (e.type === 'goal' || e.type === 'own_goal') {
      if (e.team_side === 'home') goalsHome += 1;
      else if (e.team_side === 'away') goalsAway += 1;
    } else if (e.type === 'save') {
      if (e.team_side === 'home') savesHome += 1;
      else if (e.team_side === 'away') savesAway += 1;
    } else if (foulTypes.includes(e.type)) {
      if (e.team_side === 'home') foulsHome += 1;
      else if (e.team_side === 'away') foulsAway += 1;
    }
  });
  return {
    goalsHome,
    goalsAway,
    savesHome,
    savesAway,
    foulsHome,
    foulsAway,
  };
};

const renderMMRChangeBadge = (change?: number) => {
  const rounded = Math.round(change ?? 0);
  if (rounded > 0) {
    return (
      <span className="bg-emerald-50 text-emerald-600 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
        +{rounded} MMR
      </span>
    );
  }
  if (rounded < 0) {
    return (
      <span className="bg-rose-50 text-rose-600 border border-rose-200 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
        {rounded} MMR
      </span>
    );
  }
  return (
    <span className="bg-gray-100 text-gray-500 border border-gray-200 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
      0 MMR
    </span>
  );
};

type TabId = 'timeline' | 'lineup' | 'statistik';

const ClientMabarGameRekapPage = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('timeline');

  useEffect(() => {
    if (!gameSlug) return;
    setLoading(true);
    mabarService
      .getGameBySlug(gameSlug)
      .then(setGame)
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  }, [gameSlug]);

  const timeline = useMemo(() => normalizeTimeline(game?.timeline ?? []), [game?.timeline]);
  const timelineWithoutKickOff = useMemo(
    () =>
      sortTimelineByTimeDesc(timeline.filter((e) => (e.type || '').toLowerCase() !== 'kick_off')),
    [timeline]
  );
  const { home: homeScore, away: awayScore } = useMemo(
    () => getScoreFromTimeline(timeline),
    [timeline]
  );
  const stats = useMemo(() => getStatsFromTimeline(timeline), [timeline]);

  const homeScorers = useMemo(() => {
    return timeline
      .filter((e) => e.type === 'goal' && e.team_side === 'home')
      .map((e) => ({
        name:
          game?.lineups?.find((l) => l.player_participant_id === e.player_id)?.name ??
          `Pemain #${e.player_id}`,
        time: e.time,
      }));
  }, [timeline, game?.lineups]);

  const awayScorers = useMemo(() => {
    return timeline
      .filter((e) => e.type === 'goal' && e.team_side === 'away')
      .map((e) => ({
        name:
          game?.lineups?.find((l) => l.player_participant_id === e.player_id)?.name ??
          `Pemain #${e.player_id}`,
        time: e.time,
      }));
  }, [timeline, game?.lineups]);

  if (loading) {
    return (
      <div className="space-y-4 p-6 max-w-4xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-44 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Game tidak ditemukan</p>
        <ShadcnButton variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          Kembali
        </ShadcnButton>
      </div>
    );
  }

  const statusLabel = game?.end_time ? 'FULL TIME' : game?.start_time ? 'LIVE MATCH' : 'UPCOMING';
  const homeName = game.home_team_name || 'Green Team';
  const awayName = game.away_team_name || 'Navy Team';
  const homeLineup = (game.lineups ?? []).filter((l) => l.team_side === 'home');
  const awayLineup = (game.lineups ?? []).filter((l) => l.team_side === 'away');

  return (
    <div className=" text-[#0E1726] pb-12">
      <div className="max-w-xl mx-auto px-4 md:px-6 pt-4 md:pt-6">
        {/* Desktop Navigation Header */}
        <div className="hidden md:flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-gray-100 transition-colors cursor-pointer shadow-2xs"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#0E1726]">Detail Rekap Pertandingan</h1>
            <p className="text-xs text-[#556575]">Timeline kejadian, susunan pemain, dan statistik game</p>
          </div>
        </div>

        {/* Dark Radial Gradient Banner Card (Exact User CSS Specs) */}
        <div
          style={{
            borderRadius: '18px',
            padding: '18px 16px 16px',
            background:
              'radial-gradient(120% 90% at 50% 0%, rgb(37, 49, 74) 0%, rgb(14, 23, 38) 60%)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            overflow: 'hidden',
            position: 'relative',
          }}
          className="shadow-xl text-white mb-6"
        >
          {/* Header Subtitle */}
          <div className="text-center text-[11px] font-bold text-slate-400 tracking-widest uppercase">
            GAME {game.id ? game.id : '1'} · {statusLabel}
          </div>

          {/* Teams & Score Row */}
          <div className="grid grid-cols-3 items-center text-center py-1">
            {/* Home Team */}
            <div className="flex flex-col items-center justify-center min-w-0">
              <div className="w-14 h-14 rounded-2xl bg-[#FC480D] text-white font-bold text-xl flex items-center justify-center shadow-md">
                {getTeamInitial(homeName)}
              </div>
              <span className="text-xs font-bold text-white mt-2 truncate w-full text-center">
                {homeName}
              </span>
            </div>

            {/* Score Center */}
            <div className="flex flex-col items-center justify-center">
              <span className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                {homeScore} : {awayScore}
              </span>
              <span className="bg-[#0BCF46]/20 text-[#0BCF46] text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase text-center mt-1 border border-[#0BCF46]/30">
                {homeScore === awayScore ? 'SERI' : 'FULL TIME'}
              </span>
            </div>

            {/* Away Team */}
            <div className="flex flex-col items-center justify-center min-w-0">
              <div className="w-14 h-14 rounded-2xl bg-[#25314A] border border-slate-600/80 text-white font-bold text-xl flex items-center justify-center shadow-md">
                {getTeamInitial(awayName)}
              </div>
              <span className="text-xs font-bold text-white mt-2 truncate w-full text-center">
                {awayName}
              </span>
            </div>
          </div>

          {/* Quick Highlights Row */}
          <div className="flex items-center justify-between text-xs text-slate-300 px-2 py-1.5 border-t border-slate-700/60 font-medium">
            <div className="flex items-center gap-1.5 min-w-0">
              {homeScorers.length > 0 && (
                <>
                  <Icon icon="ph:soccer-ball-fill" className="w-3.5 h-3.5 text-[#FC480D] shrink-0" />
                  <span className="truncate">{homeScorers[0].name} {homeScorers[0].time}'</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5 min-w-0 justify-end">
              {awayScorers.length > 0 && (
                <>
                  <span className="truncate">{awayScorers[0].name} {awayScorers[0].time}'</span>
                  <Icon icon="ph:soccer-ball-fill" className="w-3.5 h-3.5 text-[#FC480D] shrink-0" />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation (Clean non-card Tab Style) */}
        <div className="flex items-center border-b border-[#E0E6ED] mb-6 w-full">
          <button
            type="button"
            onClick={() => setActiveTab('timeline')}
            className={`flex-1 py-3 text-center text-sm font-bold border-b-2 transition-all cursor-pointer flex items-center justify-center gap-2 ${
              activeTab === 'timeline'
                ? 'border-[#FC480D] text-[#FC480D]'
                : 'border-transparent text-[#788290] hover:text-[#0E1726]'
            }`}
          >
            <Icon icon="ph:trend-up-bold" className="w-4 h-4" />
            Timeline
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('lineup')}
            className={`flex-1 py-3 text-center text-sm font-bold border-b-2 transition-all cursor-pointer flex items-center justify-center gap-2 ${
              activeTab === 'lineup'
                ? 'border-[#FC480D] text-[#FC480D]'
                : 'border-transparent text-[#788290] hover:text-[#0E1726]'
            }`}
          >
            <Icon icon="ph:users-three-bold" className="w-4 h-4" />
            Lineup
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('statistik')}
            className={`flex-1 py-3 text-center text-sm font-bold border-b-2 transition-all cursor-pointer flex items-center justify-center gap-2 ${
              activeTab === 'statistik'
                ? 'border-[#FC480D] text-[#FC480D]'
                : 'border-transparent text-[#788290] hover:text-[#0E1726]'
            }`}
          >
            <Icon icon="ph:chart-bar-bold" className="w-4 h-4" />
            Statistik
          </button>
        </div>

        {/* Tab Body */}
        {activeTab === 'timeline' && (
          <div className="relative pl-6 space-y-6 border-l-2 border-slate-200 ml-3 py-2">
            {timelineWithoutKickOff.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-xs font-medium">
                Belum ada data timeline kejadian
              </div>
            ) : (
              timelineWithoutKickOff.map((evt, idx) => {
                const resolveName = (id: number) =>
                  game.lineups?.find((l) => l.player_participant_id === id || l.id === id)?.name;

                const isHome = evt.team_side === 'home';
                const teamName = isHome ? homeName : awayName;
                const teamBadgeStyle = isHome
                  ? 'bg-orange-50 text-orange-600 border-orange-100'
                  : 'bg-blue-50 text-blue-600 border-blue-100';

                let evtTitle = 'Pemain';
                let evtSub = '';
                let evtIcon = <Icon icon="ph:soccer-ball-fill" className="w-4 h-4 text-emerald-600" />;
                let dotColor = isHome ? 'bg-orange-500' : 'bg-emerald-500';

                if (evt.type === 'substitution') {
                  evtTitle = 'Pergantian Pemain';
                  const nameOut =
                    (evt.player_id_out != null && resolveName(evt.player_id_out)) ??
                    (evt.player_id != null ? resolveName(evt.player_id) : undefined) ??
                    `#${evt.player_id_out ?? evt.player_id}`;
                  const nameIn =
                    (evt.player_id_in != null && resolveName(evt.player_id_in)) ??
                    `#${evt.player_id_in}`;
                  evtSub = `${nameOut} keluar, ${nameIn} masuk`;
                  evtIcon = <Icon icon="ph:arrows-left-right-bold" className="w-4 h-4 text-violet-600" />;
                  dotColor = 'bg-violet-500';
                } else {
                  if (evt.player_id != null) {
                    evtTitle = resolveName(evt.player_id) ?? `Pemain #${evt.player_id}`;
                  }

                  if (evt.type === 'own_goal' || evt.detail === 'own_goal') {
                    evtIcon = <Icon icon="ph:soccer-ball-bold" className="w-4 h-4 text-red-600" />;
                    evtSub = `Gol Bunuh Diri (OG) · Gol untuk ${teamName}`;
                    dotColor = 'bg-red-500';
                  } else if (evt.type === 'goal') {
                    evtIcon = <Icon icon="ph:soccer-ball-fill" className="w-4 h-4 text-emerald-600" />;
                    const assistName =
                      evt.assist_id != null ? resolveName(evt.assist_id) : null;
                    evtSub = assistName ? `Gol (Assist: ${assistName})` : 'Gol';
                    dotColor = isHome ? 'bg-orange-500' : 'bg-emerald-500';
                  } else if (evt.type === 'key_pass') {
                    evtIcon = <Icon icon="ph:compass-bold" className="w-4 h-4 text-sky-600" />;
                    evtSub = 'Umpan Kunci (Key Pass)';
                    dotColor = 'bg-sky-500';
                  } else if (evt.type === 'clearance') {
                    evtIcon = <Icon icon="ph:shield-check-bold" className="w-4 h-4 text-indigo-600" />;
                    evtSub = 'Sapuan Bola (Clearance)';
                    dotColor = 'bg-indigo-500';
                  } else if (evt.type === 'freestyle') {
                    evtIcon = <Icon icon="ph:sparkle-bold" className="w-4 h-4 text-amber-500" />;
                    evtSub = 'Aksi Skill (Freestyle)';
                    dotColor = 'bg-amber-400';
                  } else if (evt.type === 'save') {
                    evtIcon = <Icon icon="ph:hand-palm-fill" className="w-4 h-4 text-teal-600" />;
                    evtSub = 'Tepisan Kiper (Save)';
                    dotColor = 'bg-teal-500';
                  } else if (evt.type === 'yellow_card' || evt.detail === 'yellow_card') {
                    evtIcon = (
                      <span className="w-3 h-4 rounded-xs bg-amber-400 border border-amber-500 shadow-2xs inline-block" />
                    );
                    evtSub = 'Kartu Kuning';
                    dotColor = 'bg-yellow-400';
                  } else if (evt.type === 'red_card' || evt.detail === 'red_card') {
                    evtIcon = (
                      <span className="w-3 h-4 rounded-xs bg-red-600 border border-red-700 shadow-2xs inline-block" />
                    );
                    evtSub = 'Kartu Merah';
                    dotColor = 'bg-red-600';
                  } else if (evt.type === 'foul') {
                    evtIcon = <Icon icon="ph:warning-circle-bold" className="w-4 h-4 text-rose-500" />;
                    evtSub =
                      evt.detail === 'yellow_card'
                        ? 'Kartu Kuning'
                        : evt.detail === 'red_card'
                          ? 'Kartu Merah'
                          : evt.detail || 'Pelanggaran';
                    dotColor = 'bg-rose-500';
                  } else {
                    evtSub = evt.detail || evt.type;
                  }
                }

                return (
                  <div key={idx} className="relative flex flex-col gap-1">
                    {/* Event Dot on Timeline */}
                    <span className={`w-3 h-3 rounded-full absolute -left-[31px] top-1 border-2 border-white shadow-2xs ${dotColor}`} />
                    <span className="text-xs font-bold text-gray-400">{evt.time}'</span>

                    <div className="flex items-center gap-2">
                      {evtIcon}
                      <span className="text-xs font-bold text-[#0E1726]">{evtTitle}</span>
                    </div>

                    {evtSub && <span className="text-[11px] text-gray-400 font-medium">{evtSub}</span>}

                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border w-fit mt-0.5 ${teamBadgeStyle}`}>
                      {teamName}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'lineup' && (
          <div className="space-y-6">
            {/* Home Team Lineup */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className="w-7 h-7 rounded-full bg-[#FC480D] text-white font-bold text-xs flex items-center justify-center">
                  {getTeamInitial(homeName)}
                </span>
                <span className="font-bold text-sm text-[#FC480D]">{homeName}</span>
              </div>
              <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs divide-y divide-[#E0E6ED] overflow-hidden">
                {homeLineup.length > 0 ? (
                  homeLineup.map((p, idx) => (
                    <div key={p.id || idx} className="p-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-gray-100 font-bold text-xs flex items-center justify-center text-gray-700 shrink-0">
                          {p.player_number ?? idx + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-[#0E1726] truncate flex items-center gap-1">
                            <span>{p.name}</span>
                            {p.is_captain && (
                              <span className="text-[10px] text-orange-500 font-bold bg-orange-50 border border-orange-200 px-1 rounded">
                                c
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] text-gray-400">{p.position || 'Player'}</p>
                        </div>
                      </div>
                      {renderMMRChangeBadge(p.mmr_change)}
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 p-4 text-center">Belum ada data lineup</p>
                )}
              </div>
            </div>

            {/* Away Team Lineup */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className="w-7 h-7 rounded-full bg-[#25314A] text-white font-bold text-xs flex items-center justify-center">
                  {getTeamInitial(awayName)}
                </span>
                <span className="font-bold text-sm text-[#25314A]">{awayName}</span>
              </div>
              <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs divide-y divide-[#E0E6ED] overflow-hidden">
                {awayLineup.length > 0 ? (
                  awayLineup.map((p, idx) => (
                    <div key={p.id || idx} className="p-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-gray-100 font-bold text-xs flex items-center justify-center text-gray-700 shrink-0">
                          {p.player_number ?? idx + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-[#0E1726] truncate flex items-center gap-1">
                            <span>{p.name}</span>
                            {p.is_captain && (
                              <span className="text-[10px] text-orange-500 font-bold bg-orange-50 border border-orange-200 px-1 rounded">
                                c
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] text-gray-400">{p.position || 'Player'}</p>
                        </div>
                      </div>
                      {renderMMRChangeBadge(p.mmr_change)}
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 p-4 text-center">Belum ada data lineup</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'statistik' && (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-5 shadow-2xs space-y-5 max-w-xl mx-auto">
            <StatProgressRow
              label="Gol"
              homeVal={stats.goalsHome}
              awayVal={stats.goalsAway}
            />
            <StatProgressRow
              label="Penyelamatan"
              homeVal={stats.savesHome}
              awayVal={stats.savesAway}
            />
            <StatProgressRow
              label="Pelanggaran"
              homeVal={stats.foulsHome}
              awayVal={stats.foulsAway}
            />
          </div>
        )}
      </div>
    </div>
  );
};

function StatProgressRow({
  label,
  homeVal,
  awayVal,
  isPercentage = false,
}: {
  label: string;
  homeVal: number;
  awayVal: number;
  isPercentage?: boolean;
}) {
  const total = homeVal + awayVal;
  const homePct = total > 0 ? Math.round((homeVal / total) * 100) : 50;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold text-[#FC480D]">
          {homeVal}
          {isPercentage ? '%' : ''}
        </span>
        <span className="text-gray-400 font-medium text-[11px]">{label}</span>
        <span className="font-bold text-[#0E1726]">
          {awayVal}
          {isPercentage ? '%' : ''}
        </span>
      </div>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
        <div
          className="h-full bg-[#FC480D] transition-all rounded-l-full"
          style={{ width: `${homePct}%` }}
        />
        <div
          className="h-full bg-[#E0E6ED] transition-all rounded-r-full"
          style={{ width: `${100 - homePct}%` }}
        />
      </div>
    </div>
  );
}

export default ClientMabarGameRekapPage;
